import { ErrorBoundary } from '@sentry/react';
import { Stacked } from '@statsbomb/kitbag-components';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { playerRadarTemplateConfigsAtom } from '@/atoms/queries/userConfigs';
import { ContentState } from '@/components/contentState/ContentState';
import SummaryProfileDropdown from '@/components/radar/SummaryProfileDropdown';
import { useRightPanelHeader } from '@/hooks/useRightPanelHeader/useRightPanelHeader';
import { StatusTranslationKey } from '@/types/status';
import { PlayerDashboardSummaryContent } from './PlayerDashboardSummaryContent';

export const PlayerDashboardSummaryPanel = () => {
  const { t } = useTranslation(['navigation', 'radar']);
  const navigate = useNavigate();
  const handleClose = useCallback(() => navigate('..'), [navigate]);

  const panelRef = useRef<HTMLDivElement>(null);

  useRightPanelHeader(t('secondary.summary', { ns: 'navigation' }), handleClose);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <div ref={panelRef} tabIndex={-1} style={{ outline: 'none' }} className="relative p-4 h-full">
      <Stacked direction="column" gap="medium">
        {/* There is pre-existing custom styling within this project. Follow up required to handle styling inside component library */}
        <div className="w-56 shrink-0">
          <SummaryProfileDropdown
            radarTemplateConfigsAtom={playerRadarTemplateConfigsAtom}
            label={t('summaryProfileDropdownLabel', { ns: 'radar' })}
          />
        </div>
        <ErrorBoundary fallback={<ContentState status={StatusTranslationKey.ERROR} />}>
          <PlayerDashboardSummaryContent />
        </ErrorBoundary>
      </Stacked>
    </div>
  );
};
